import React, { useState, useEffect } from 'react';
import { Modal, Form, InputNumber, DatePicker, Alert, Descriptions, Badge, message, Spin } from 'antd';
import moment from 'moment';
import 'moment-timezone';
import { TZ, endOfDayTz, toYMD } from '../../../utils/tz';
import { getFactsForDate } from '../api';

/**
 * Модальное окно для редактирования фактов менеджера за конкретную дату
 * @param {Object} props
 * @param {Object} props.manager - данные менеджера { id, name }
 * @param {Function} props.onSave - callback при сохранении (managerId, date, facts)
 * @param {Function} props.onCancel - callback при отмене
 * @param {boolean} props.visible - видимость модального окна
 * @param {Object} props.currentFacts - текущие факты для отображения (необязательно)
 * @param {string} props.currency - валюта для отображения
 */
const EditFactsModal = ({ manager, onSave, onCancel, visible, currentFacts = {}, currency = 'KZT' }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(moment.tz(TZ));
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [prefilled, setPrefilled] = useState(false);

  useEffect(() => {
    if (visible) {
      // Не сбрасываем форму при открытии, чтобы значения не мигали
      setSelectedDate(manager?.initialDate ? moment(manager.initialDate).tz(TZ) : moment.tz(TZ));
      setPrefilled(false);

      // Начальное заполнение из currentFacts (если есть)
      const toNum = (v) => (v === null || v === undefined || v === '' ? undefined : Number(v));
      if (currentFacts && Object.keys(currentFacts).length > 0) {
        const initial = {
          leads: toNum(currentFacts.leads),
          qualified: toNum(currentFacts.qualified),
          measure: toNum(currentFacts.measure),
          salesCount: toNum(currentFacts.salesCount),
          salesSum: toNum(currentFacts.salesSum),
        };
        form.setFieldsValue(initial);
        setPrefilled(true);
        // debug
        // eslint-disable-next-line no-console
        console.debug('Prefill from currentFacts:', initial);
      }
    }
  }, [visible, form, currentFacts]);

  // Подгружаем факты при смене даты
  useEffect(() => {
    const load = async () => {
      if (!visible || !manager?.id || !selectedDate) return;
      setPrefillLoading(true);
      try {
        const dateStr = toYMD(selectedDate);
        const facts = await getFactsForDate(manager.id, dateStr);
        const toNum = (v) => (v === null || v === undefined || v === '' ? undefined : Number(v));
        if (facts) {
          form.setFieldsValue({
            leads: toNum(facts.leads),
            qualified: toNum(facts.qualified),
            measure: toNum(facts.measure),
            salesCount: toNum(facts.salesCount),
            salesSum: toNum(facts.salesSum),
          });
          setPrefilled(true);
          // eslint-disable-next-line no-console
          console.debug('Prefill from API for date', dateStr, facts);
        } else {
          // если нет данных — оставляем текущие значения (не затираем нулями)
          // eslint-disable-next-line no-console
          console.debug('No facts for date, keep current form values');
        }
      } finally {
        setPrefillLoading(false);
      }
    };
    load();
  }, [visible, manager?.id, selectedDate, form]);

  const formatCurrency = (value) => {
    try {
      return new Intl.NumberFormat('ru-RU', {
        style: 'currency',
        currency: currency || 'KZT',
        maximumFractionDigits: 0,
      }).format(value || 0);
    } catch (e) {
      return `${value || 0} ${currency || '₸'}`;
    }
  };

  const formatNumber = (value) => {
    try {
      return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(Number(value || 0));
    } catch (e) {
      return String(value || 0);
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const facts = {
        leads: values.leads ?? null,
        qualified: values.qualified ?? null,
        measure: values.measure ?? null,
        salesCount: values.salesCount ?? null,
        salesSum: values.salesSum ?? null,
      };

      // Фильтруем null значения (не заполненные поля)
      const filteredFacts = Object.fromEntries(
        Object.entries(facts).filter(([_, v]) => v !== null)
      );

      if (Object.keys(filteredFacts).length === 0) {
        message.warning('Заполните хотя бы одно поле для сохранения');
        setLoading(false);
        return;
      }

      await onSave(manager.id, toYMD(selectedDate), filteredFacts);
      message.success('Факты успешно сохранены');
      form.resetFields();
      onCancel();
    } catch (error) {
      console.error('Ошибка сохранения фактов:', error);
      message.error('Не удалось сохранить факты');
    } finally {
      setLoading(false);
    }
  };

  const disabledDate = (current) => {
    // Нельзя выбрать дату в будущем
    return current && current > endOfDayTz(moment.tz(TZ));
  };

  // Удаление ведущих нулей при вводе
  const stripLeadingZerosParser = (raw) => {
    const str = (raw ?? '').toString().replace(/\s/g, '');
    const cleaned = str.replace(/^0+(?=\d)/, '');
    if (cleaned === '') return null;
    const num = Number(cleaned);
    return Number.isNaN(num) ? null : num;
  };

  return (
    <Modal
      title={`Редактирование фактов: ${manager?.name || 'Менеджер'}`}
      visible={visible}
      onOk={handleOk}
      onCancel={onCancel}
      confirmLoading={loading}
      width={700}
      okText="Сохранить"
      cancelText="Отмена"
      destroyOnClose={true}
      maskClosable={false}
      style={{ zIndex: 2000 }}
      getContainer={() => document.body}
      forceRender={true}
    >
      <div style={{ padding: '20px', background: 'white' }}>
        <Alert
          message="Внимание"
          description="Вы редактируете факты вручную. Эти данные будут приоритетнее автоматически загруженных данных и будут отмечены специальным индикатором."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />

        {prefilled && (
          <Alert
            message="Значения формы загружены за выбранную дату"
            type="success"
            showIcon
            style={{ marginBottom: 12 }}
          />
        )}

        <Spin spinning={prefillLoading}>
          <Form form={form} layout="vertical" preserve={false}>
            <Form.Item
              label="Дата"
              name="date"
              rules={[{ required: true, message: 'Выберите дату' }]}
            >
              <DatePicker
                format="DD.MM.YYYY"
                disabledDate={disabledDate}
                value={selectedDate}
                onChange={(d) => { setSelectedDate(d); form.setFieldsValue({ date: d }); }}
                style={{ width: '100%' }}
                placeholder="Выберите дату"
              />
            </Form.Item>

            {Object.keys(currentFacts).length > 0 && (
              <Descriptions
                title="Текущие значения (для справки)"
                bordered
                size="small"
                column={2}
                style={{ marginBottom: 16 }}
              >
                {currentFacts.leads !== undefined && (
                  <Descriptions.Item label="Лиды">
                    {formatNumber(currentFacts.leads)}
                    {currentFacts.leadsManual && <Badge status="warning" text="Ручной" style={{ marginLeft: 8 }} />}
                  </Descriptions.Item>
                )}
                {currentFacts.qualified !== undefined && (
                  <Descriptions.Item label="Квал. лиды">
                    {formatNumber(currentFacts.qualified)}
                    {currentFacts.qualifiedManual && <Badge status="warning" text="Ручной" style={{ marginLeft: 8 }} />}
                  </Descriptions.Item>
                )}
                {currentFacts.measure !== undefined && (
                  <Descriptions.Item label="Замеры">
                    {formatNumber(currentFacts.measure)}
                    {currentFacts.measureManual && <Badge status="warning" text="Ручной" style={{ marginLeft: 8 }} />}
                  </Descriptions.Item>
                )}
                {currentFacts.salesCount !== undefined && (
                  <Descriptions.Item label="Продажи">
                    {formatNumber(currentFacts.salesCount)}
                    {currentFacts.salesCountManual && <Badge status="warning" text="Ручной" style={{ marginLeft: 8 }} />}
                  </Descriptions.Item>
                )}
                {currentFacts.salesSum !== undefined && (
                  <Descriptions.Item label="Сумма продаж">
                    {formatCurrency(currentFacts.salesSum)}
                    {currentFacts.salesSumManual && <Badge status="warning" text="Ручной" style={{ marginLeft: 8 }} />}
                  </Descriptions.Item>
                )}
              </Descriptions>
            )}

            <Alert
              message="Оставьте поле пустым, если не хотите его изменять"
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Form.Item label="Лиды" name="leads">
              <InputNumber
                min={0}
                parser={stripLeadingZerosParser}
                style={{ width: '100%' }}
                placeholder="Введите количество лидов"
              />
            </Form.Item>

            <Form.Item label="Квалифицированные лиды" name="qualified">
              <InputNumber
                min={0}
                parser={stripLeadingZerosParser}
                style={{ width: '100%' }}
                placeholder="Введите количество квалифицированных лидов"
              />
            </Form.Item>

            <Form.Item label="Замеры" name="measure">
              <InputNumber
                min={0}
                parser={stripLeadingZerosParser}
                style={{ width: '100%' }}
                placeholder="Введите количество замеров"
              />
            </Form.Item>

            <Form.Item label="Количество продаж" name="salesCount">
              <InputNumber
                min={0}
                parser={stripLeadingZerosParser}
                style={{ width: '100%' }}
                placeholder="Введите количество продаж"
              />
            </Form.Item>

            <Form.Item label="Сумма продаж" name="salesSum">
              <InputNumber
                min={0}
                step={10000}
                parser={stripLeadingZerosParser}
                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                style={{ width: '100%' }}
                placeholder="Введите сумму продаж"
              />
            </Form.Item>

            {/* Средний чек удалён из редактирования, так как рассчитывается автоматически */}
          </Form>
        </Spin>
      </div>
    </Modal>
  );
};

export default EditFactsModal;

